/**
 * Profile Manager Service
 * Handles all API calls for comprehensive profile management
 */

import axios from 'axios';
import { API_URL as API_BASE_URL } from '../config';

// Configure axios defaults
axios.defaults.withCredentials = true;

/**
 * Get complete user profile
 */
export const getProfile = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}/api/profile-manager`);
    return response.data;
  } catch (error) {
    console.error('Error fetching profile:', error);
    throw error;
  }
};

/**
 * Update complete profile
 */
export const updateProfile = async (profileData) => {
  try {
    const response = await axios.put(
      `${API_BASE_URL}/api/profile-manager`,
      profileData
    );
    return response.data;
  } catch (error) {
    console.error('Error updating profile:', error);
    throw error;
  }
};

/**
 * Update personal information only
 */
export const updatePersonalInfo = async (personalInfo) => {
  try {
    const response = await axios.patch(
      `${API_BASE_URL}/api/profile-manager/personal`,
      personalInfo
    );
    return response.data;
  } catch (error) {
    console.error('Error updating personal info:', error);
    throw error;
  }
};

/**
 * Add a new summary
 */
export const addSummary = async (summary) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/api/profile-manager/summaries`,
      summary
    );
    return response.data;
  } catch (error) {
    console.error('Error adding summary:', error);
    throw error;
  }
};

/**
 * Update a specific summary
 */
export const updateSummary = async (index, summary) => {
  try {
    const response = await axios.put(
      `${API_BASE_URL}/api/profile-manager/summaries/${index}`,
      summary
    );
    return response.data;
  } catch (error) {
    console.error('Error updating summary:', error);
    throw error;
  }
};

/**
 * Delete a summary
 */
export const deleteSummary = async (index) => {
  try {
    await axios.delete(
      `${API_BASE_URL}/api/profile-manager/summaries/${index}`
    );
  } catch (error) {
    console.error('Error deleting summary:', error);
    throw error;
  }
};

/**
 * Add new work experience
 */
export const addExperience = async (experience) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/api/profile-manager/experience`,
      experience
    );
    return response.data;
  } catch (error) {
    console.error('Error adding experience:', error);
    throw error;
  }
};

/**
 * Update work experience
 */
export const updateExperience = async (index, experience) => {
  try {
    const response = await axios.put(
      `${API_BASE_URL}/api/profile-manager/experience/${index}`,
      experience
    );
    return response.data;
  } catch (error) {
    console.error('Error updating experience:', error);
    throw error;
  }
};

/**
 * Delete work experience
 */
export const deleteExperience = async (index) => {
  try {
    await axios.delete(
      `${API_BASE_URL}/api/profile-manager/experience/${index}`
    );
  } catch (error) {
    console.error('Error deleting experience:', error);
    throw error;
  }
};

/**
 * Add education entry
 */
export const addEducation = async (education) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/api/profile-manager/education`,
      education
    );
    return response.data;
  } catch (error) {
    console.error('Error adding education:', error);
    throw error;
  }
};

/**
 * Update education entry
 */
export const updateEducation = async (index, education) => {
  try {
    const response = await axios.put(
      `${API_BASE_URL}/api/profile-manager/education/${index}`,
      education
    );
    return response.data;
  } catch (error) {
    console.error('Error updating education:', error);
    throw error;
  }
};

/**
 * Delete education entry
 */
export const deleteEducation = async (index) => {
  try {
    await axios.delete(
      `${API_BASE_URL}/api/profile-manager/education/${index}`
    );
  } catch (error) {
    console.error('Error deleting education:', error);
    throw error;
  }
};

/**
 * Update skills section
 */
export const updateSkills = async (skills) => {
  try {
    const response = await axios.patch(
      `${API_BASE_URL}/api/profile-manager/skills`,
      skills
    );
    return response.data;
  } catch (error) {
    console.error('Error updating skills:', error);
    throw error;
  }
};

/**
 * Add a project
 */
export const addProject = async (project) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/api/profile-manager/projects`,
      project
    );
    return response.data;
  } catch (error) {
    console.error('Error adding project:', error);
    throw error;
  }
};

/**
 * Export profile as JSON
 */
export const exportProfile = async () => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/api/profile-manager/export`
    );
    return response.data;
  } catch (error) {
    console.error('Error exporting profile:', error);
    throw error;
  }
};

/**
 * Import profile from JSON
 */
export const importProfile = async (profileData) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/api/profile-manager/import`,
      profileData
    );
    return response.data;
  } catch (error) {
    console.error('Error importing profile:', error);
    throw error;
  }
};

/**
 * Download profile as JSON file
 */
export const downloadProfileJSON = async () => {
  try {
    const profile = await exportProfile();
    const dataStr = JSON.stringify(profile, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = `profile_${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error downloading profile:', error);
    throw error;
  }
};

/**
 * Upload and import profile from JSON file
 */
export const uploadProfileJSON = async (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = async (e) => {
      try {
        const profileData = JSON.parse(e.target.result);
        const result = await importProfile(profileData);
        resolve(result);
      } catch (error) {
        reject(error);
      }
    };
    
    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };
    
    reader.readAsText(file);
  });
};
